// src/components/Dashboard/GalleryView.tsx
/**
 * Gallery View — shows image thumbnails from a dashboard/project.
 * Based on the AssetsTab pattern (HomeView) but uses the
 * GetGalleryFileProject API scoped to a dashboard.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { AssetCard } from '@/components/AssetCard/AssetCard';
import { ImagesGallery, type Attachment } from '@/components/ImagesGallery';
import type { PersonFile } from '@/services/homeService';

const PAGE_SIZE = 60;

export interface GalleryViewProps {
  dashboardId: number;
  projectId: string;
  filters: string;
  searchText: string;
  extraFilterParams?: Record<string, any>;
  onOpenInfoWindow: (conversationId: number) => void;
  showAllImages: boolean;
}

export const GalleryView: React.FC<GalleryViewProps> = ({
  dashboardId,
  projectId,
  filters,
  searchText,
  extraFilterParams,
  onOpenInfoWindow,
  showAllImages,
}) =>
{
  const [files, setFiles] = useState<PersonFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noAssets, setNoAssets] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const typeFilter = showAllImages ? 0 : 2;

  // Load data when dashboard/filters/search/typeFilter change
  useEffect(() =>
  {
    let cancelled = false;

    const load = async () =>
    {
      try
      {
        setLoading(true);
        setNoAssets(false);

        const response = await dashboardApi.getGalleryFileProject({
          CustomFields: [],
          Option: 1,
          PageNumber: 1,
          PageSize: PAGE_SIZE,
          DashboardId: dashboardId,
          Filters: filters,
          SearchText: searchText,
          LayersToHide: '',
          typeFilter,
          Project: projectId,
          ...extraFilterParams,
        });

        if (cancelled) return;

        const withThumbnails = response.filter(f => f.Thumbnail);

        if (withThumbnails.length === 0)
        {
          setNoAssets(true);
        }

        setFiles(withThumbnails);
        setHasMore(response.length >= PAGE_SIZE);
        setPageNumber(1);
      }
      catch (error)
      {
        console.error('Error loading gallery files:', error);
        if (!cancelled) setNoAssets(true);
      }
      finally
      {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () =>
    {
      cancelled = true;
    };
  }, [dashboardId, filters, searchText, projectId, extraFilterParams, typeFilter]);

  // Load more (infinite scroll)
  const loadMore = useCallback(async () =>
  {
    if (!hasMore || loadingMore) return;

    try
    {
      setLoadingMore(true);
      const nextPage = pageNumber + 1;

      const response = await dashboardApi.getGalleryFileProject({
        CustomFields: [],
        Option: 1,
        PageNumber: nextPage,
        PageSize: PAGE_SIZE,
        DashboardId: dashboardId,
        Filters: filters,
        SearchText: searchText,
        LayersToHide: '',
        typeFilter,
        Project: projectId,
        ...extraFilterParams,
      });

      const withThumbnails = response.filter(f => f.Thumbnail);

      if (withThumbnails.length > 0)
      {
        setFiles(prev => [...prev, ...withThumbnails]);
        setPageNumber(nextPage);
        setHasMore(response.length >= PAGE_SIZE);
      }
      else
      {
        setHasMore(false);
      }
    }
    catch (error)
    {
      console.error('Error loading more gallery files:', error);
    }
    finally
    {
      setLoadingMore(false);
    }
  }, [
    hasMore,
    loadingMore,
    pageNumber,
    dashboardId,
    filters,
    searchText,
    projectId,
    extraFilterParams,
    typeFilter,
  ]);

  // IntersectionObserver — triggers loadMore when the sentinel at the bottom comes into view
  useEffect(() =>
  {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      entries =>
      {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, hasMore]);

  // Map PersonFile[] → Attachment[] for ImagesGallery
  const galleryAttachments = useMemo<Attachment[]>(
    () =>
      files.map(f => ({
        AttachmentId: f.AttachmentId,
        EmbeddedImageId: f.EmbeddedImageId ?? undefined,
        FileUrl: f.FileUrl,
        Thumbnail: { FileUrl: f.Thumbnail?.FileUrl || f.FileUrl },
        Description: f.Description,
        FileName: f.FileName,
        FileExtension: f.FileExtension,
        Favorite: f.Favorite,
      })),
    [files],
  );

  const handleThumbnailClick = useCallback(
    (file: PersonFile) =>
    {
      const idx = files.findIndex(
        f => f.AttachmentId === file.AttachmentId && f.EmbeddedImageId === file.EmbeddedImageId,
      );
      setGalleryIndex(idx >= 0 ? idx : 0);
      setGalleryOpen(true);
    },
    [files],
  );

  const handleGalleryClose = useCallback(() =>
  {
    setGalleryOpen(false);
  }, []);

  const handleMarkImage = useCallback(
    (attachmentId: number, embeddedImageId: number, isFavorite: boolean) =>
    {
      setFiles(prev =>
        prev.map(f =>
        {
          const isMatch =
            (attachmentId > 0 && f.AttachmentId === attachmentId) ||
            (embeddedImageId > 0 && f.EmbeddedImageId === embeddedImageId);
          if (isMatch)
          {
            return { ...f, Favorite: isFavorite ? 1 : 0 };
          }
          return f;
        }),
      );
    },
    [],
  );

  const handleTitleClick = useCallback(
    (conversationId: number) =>
    {
      onOpenInfoWindow(conversationId);
    },
    [onOpenInfoWindow],
  );

  const isDark = document.documentElement.classList.contains('dark');

  // Loading skeleton
  if (loading && files.length === 0)
  {
    return (
      <div className="assets-tab-content h-full overflow-hidden p-4">
        <div className="project-files-items gallery-tab-items">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div
              key={i}
              className="project-files-item animate-pulse"
              style={{ overflow: 'hidden' }}
            >
              <div style={{ paddingBottom: '62.5%', backgroundColor: '#e5e7eb' }} />
              <div style={{ borderTop: '1px solid #ddd', padding: 4 }}>
                <div
                  style={{
                    height: 10,
                    backgroundColor: isDark ? '#4a4c4c' : '#e5e7eb',
                    borderRadius: 2,
                    width: '75%',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (noAssets && files.length === 0)
  {
    return (
      <div className="assets-tab-content h-full flex items-center justify-center">
        <div className="text-center py-16">
          <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No images to display</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="user-attachments user-attachments-tab-view"
      style={{ backgroundColor: '#ededed' }}
    >
      <div className="project-files-items gallery-tab-items" style={{ paddingBottom: 30 }}>
        {files.map((file, index) => (
          <AssetCard
            key={`${file.AttachmentId || file.EmbeddedImageId}-${index}`}
            file={file}
            onClickThumbnail={handleThumbnailClick}
            onClickTitle={handleTitleClick}
          />
        ))}
      </div>

      {/* Loading more indicator */}
      {loadingMore && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#5dade2] mx-auto" />
        </div>
      )}

      {/* Sentinel — IntersectionObserver watches this to trigger pagination */}
      {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}

      {/* No more items */}
      {!hasMore && files.length > 0 && (
        <div className="text-center py-4">
          <span className="text-gray-400 text-sm">No more images.</span>
        </div>
      )}

      <ImagesGallery
        open={galleryOpen}
        attachments={galleryAttachments}
        index={galleryIndex}
        onClose={handleGalleryClose}
        onMarkImage={handleMarkImage}
      />
    </div>
  );
};

export default GalleryView;
